import { useCallback, useEffect, useState } from 'react';
import { ApiError, childMessages, type ChildMessagingDto, type PrivateMessageDto } from '../net/api';

/** Family page: who the child may message, group approvals, and reading their chats. */
export function ChildMessaging({ childId, username }: { childId: number; username: string }) {
  const [d, setD] = useState<ChildMessagingDto>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [reading, setReading] = useState<{ id: number; name: string; rows: PrivateMessageDto[] }>();
  const load = useCallback(() => childMessages.overview(childId).then(setD, (e: Error) => setMsg({ kind: 'err', text: e.message })), [childId]);
  useEffect(() => {
    void load();
  }, [load]);
  if (!d) return <p className="muted">{msg?.text ?? 'Loading…'}</p>;
  if (!d.canMessage) return <p className="muted small-print">Private messages are for players 13 and over, so {username} can’t use them yet.</p>;

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      setMsg({ kind: 'ok', text: ok });
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  };
  async function allow(f: ChildMessagingDto['friends'][number]) {
    try {
      await childMessages.setContact(childId, f.userId, true);
    } catch (e) {
      // Outside the age group (or an adult): show their age and ask before allowing.
      if (e instanceof ApiError && e.code === 'confirm_age') {
        if (!window.confirm(`${e.message}\n\nAllow ${username} to message ${f.username}?`)) return;
        await childMessages.setContact(childId, f.userId, true, true);
      } else return setMsg({ kind: 'err', text: (e as Error).message });
    }
    setMsg({ kind: 'ok', text: `${username} can now message ${f.username}.` });
    await load();
  }

  if (reading) {
    return (
      <div className="stack">
        <button className="btn small ghost" onClick={() => setReading(undefined)}>← Back</button>
        <h3>{reading.name}</h3>
        <div className="messages-log">
          {reading.rows.map((m) => (
            <div key={m.id} className={`pm ${m.status}`}>
              <span className="pm-from">{m.from}</span>
              <span className="pm-text">{m.text}</span>
              <span className="muted small-print">{m.status === 'blocked' ? 'not sent' : new Date(m.at).toLocaleString()}</span>
            </div>
          ))}
          {!reading.rows.length && <p className="muted small-print">No messages yet.</p>}
        </div>
      </div>
    );
  }
  return (
    <div className="stack">
      <p className="muted small-print">
        {d.age !== null && d.age <= 14
          ? `${username} can message you, and friends you allow below.`
          : `${username} can message friends their own age once their account is verified; you can also allow particular friends below.`}{' '}
        You can read all of {username}’s private messages, and their chats show a “monitored by a parent” notice.
      </p>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {d.waiting.length > 0 && (
        <section className="stack">
          <h3>Waiting for you</h3>
          {d.waiting.map((w) => (
            <div key={`${w.conversationId}-${w.userId}`} className="row-wrap">
              <span>
                Group “{w.groupName}”: {w.userId === childId ? `${username} was added` : `${w.username}${w.age !== null ? ` (${w.age}${w.age >= 18 ? ', an adult' : ''})` : ''} wants to join`}
              </span>
              <button className="btn small" onClick={() => void act(() => childMessages.decideMember(childId, w.conversationId, w.userId, true), 'Approved.')}>Approve</button>
              <button className="btn small ghost" onClick={() => void act(() => childMessages.decideMember(childId, w.conversationId, w.userId, false), 'Turned down.')}>No</button>
            </div>
          ))}
        </section>
      )}
      <section className="stack">
        <h3>Friends {username} may message</h3>
        {!d.friends.length && <p className="muted small-print">{username} has no friends yet.</p>}
        {d.friends.map((f) => (
          <div key={f.userId} className="row-wrap">
            <span>
              {f.username} <span className="muted small-print">{f.age !== null ? `${f.age}${f.adult ? ', adult' : ''}` : ''}{!f.sameGroup ? ' · outside their age group' : ''}</span>
            </span>
            {f.approved ? (
              <button className="btn small ghost" onClick={() => void act(() => childMessages.setContact(childId, f.userId, false), `${username} can no longer message ${f.username} (unless both are verified and the same age group).`)}>Stop allowing</button>
            ) : (
              <button className="btn small" onClick={() => void allow(f)}>Allow messaging</button>
            )}
          </div>
        ))}
      </section>
      {d.ownedGroups.length > 0 && (
        <section className="stack">
          <h3>Groups {username} runs</h3>
          {d.ownedGroups.map((g) => (
            <label key={g.id} className="check">
              <input type="checkbox" checked={g.ageRule === 'open'} onChange={(e) => void act(() => childMessages.setAgeRule(childId, g.id, e.target.checked), 'Saved.')} /> “{g.name}”: allow people outside {username}’s age group (you approve each one)
            </label>
          ))}
        </section>
      )}
      <section className="stack">
        <h3>{username}’s chats</h3>
        {!d.conversations.length && <p className="muted small-print">No chats yet.</p>}
        {d.conversations.map((c) => (
          <button key={c.id} className="link-btn" onClick={() => void childMessages.read(childId, c.id).then((r) => setReading({ id: c.id, name: c.name ?? 'Chat', rows: r.messages }))}>
            {c.name} <span className="muted small-print">({c.members.length} people)</span>
          </button>
        ))}
      </section>
    </div>
  );
}
